import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { Note, Steps, Tick, iso, printTime } from '../ui/actions/parts.js';
import { Segments } from '../ui/kit.js';
import { useBuilding, RESIDENT, printDate } from '../state/building.js';

import GR86 from '../media/images/car-gr86.jpg';
import CaymanGT4RS from '../media/images/car-gt4rs.jpg';
import Emira from '../media/images/car-emira.webp';

const EASE = [0.16, 1, 0.3, 1];

/* The resident's vehicles, in the order they appear.
 *
 * They used to be two identical "Request Your Car" panels with nothing to
 * tell them apart, which was survivable at two and is not at three, so
 * each one now names itself. */
/* `focus` is the point of each photograph to keep when the card crops it.
   All three are wide three-quarter shots, but the car does not sit in the
   same place in each frame. */
const CARS = [
    { id: 'gr86',  name: 'Toyota GR86',               short: 'GR86',     colour: 'Neptune',     space: 'Space 2-14', focus: '50% 55%', image: GR86 },
    { id: 'gt4rs', name: 'Porsche 718 Cayman GT4 RS', short: 'GT4 RS',   colour: 'Arctic Grey', space: 'Space 2-15', focus: '45% 60%', image: CaymanGT4RS },
    { id: 'emira', name: 'Lotus Emira',               short: 'Emira',    colour: 'Green',       space: 'Space 2-16', focus: '40% 55%', image: Emira }
];

/* A live request runs on demo time: the eight minutes a real retrieval
   takes play out in about twenty seconds, so a walkthrough can watch it
   finish. `at` is when each step starts, in seconds from the tap. */
const RUN_SECONDS = 22;
const ETA_MINUTES = 8;
const STEPS = [
    { label: 'Requested', at: 0 },
    { label: 'Car retrieved', at: 6 },
    { label: 'On the way up', at: 13 },
    { label: 'Ready at the front entrance', at: RUN_SECONDS }
];

/* The short form of each step, for the chip on the car's bay. */
const STAGE = ['Requested', 'Retrieved', 'On the way up', 'At the door'];

const VALETS = ['Marco', 'Andrés', 'Kendra'];
const TIPS = ['$5', '$10', '$20'];

/* The request outlives the screen. Leaving Valet to check a package and
   coming back should find the car still on its way, not forgotten, so
   it is kept at module level rather than in the component's state. */
let liveRequest = null;

const SERVICES = [
    {
        key: 'pickup',
        title: 'Schedule Car Pickup',
        copy: 'Select a date and time for your car to be ready.',
        fields: [
            { name: 'date', type: 'date', label: 'Pickup date' },
            { name: 'time', type: 'time', label: 'Pickup time' }
        ],
        action: 'Schedule Pickup',
        missing: 'Choose a date and a time for the pickup.',
        confirm: (v, car) => `Pickup scheduled for ${printDate(v.date)} at ${printTime(v.time)}. Your ${car.short} will be waiting out front.`
    },
    {
        key: 'guest',
        title: 'Reserve Valet for Guest',
        copy: "Reserve a valet spot for your guest's car.",
        fields: [
            { name: 'name', type: 'text', placeholder: 'Guest Name' },
            { name: 'vehicle', type: 'text', placeholder: 'Guest Vehicle' },
            { name: 'date', type: 'date', label: 'Guest arrival date' }
        ],
        action: 'Reserve Valet',
        missing: "Add your guest's name, their car and the day they arrive.",
        confirm: v => `Valet spot reserved for ${v.name} on ${printDate(v.date)}. The front desk has them on the guest list.`
    },
    {
        key: 'cleaning',
        title: 'Car Cleaning Services',
        copy: 'Request a car wash or interior cleaning before your car is returned.',
        select: {
            name: 'service',
            placeholder: 'Select Cleaning Service',
            options: ['Exterior Wash · $45', 'Interior Vacuum · $35', 'Full Detailing · $145']
        },
        action: 'Request Cleaning',
        missing: 'Choose a cleaning service first.',
        confirm: (v, car) => {
            const [name, price] = v.service.split(' · ');
            return `${name} booked for your ${car.short}, ${price} to your unit account. It will be done before the car comes up.`;
        }
    },
    {
        key: 'tires',
        title: 'Tire Pressure Check',
        copy: 'Ensure your tires are properly inflated before you pick up your car.',
        action: 'Request Tire Check',
        confirm: (v, car) => `Tire check requested. Your ${car.short} will be set to its door-jamb pressures today.`
    },
    {
        key: 'fuel',
        title: 'Fuel Top-Up Service',
        copy: 'Request a fuel top-up while your car is with the valet.',
        fields: [{ name: 'gallons', type: 'number', placeholder: 'Amount (gallons)', min: 1, max: 20 }],
        action: 'Request Fuel Top-Up',
        missing: 'Enter an amount between 1 and 20 gallons.',
        valid: v => Number(v.gallons) >= 1 && Number(v.gallons) <= 20,
        confirm: (v, car) => `${Number(v.gallons)} ${Number(v.gallons) === 1 ? 'gallon' : 'gallons'} of premium requested for your ${car.short}.`
    },
    {
        key: 'notes',
        title: 'Special Instructions',
        copy: 'Add any specific instructions for the valet team.',
        textarea: { name: 'notes', placeholder: 'Add instructions here…' },
        action: 'Submit Instructions',
        missing: 'Write your instructions first.',
        confirm: (v, car) => `Instructions sent. They are on your ${car.short}'s ticket for every valet on shift.`
    }
];

function ValetScreen() {
    /* Which car the services below act on. There is no carousel any more:
       a resident with three cars wants to see the three and choose, not
       page through them behind arrows, and the wide display has room for
       all of them. Choosing one also gives the service cards underneath a
       subject; "Schedule Car Pickup" never said which car it meant. */
    const [selected, setSelected] = useState(liveRequest ? liveRequest.carId : CARS[0].id);
    const [live, setLive] = useState(liveRequest);
    const [note, setNote] = useState(null);
    const [stage, setStage] = useState(STAGE[0]);
    const reduced = useReducedMotion();
    const car = CARS.find(c => c.id === selected) || CARS[0];

    const request = () => {
        liveRequest = {
            carId: car.id,
            startedAt: Date.now(),
            valet: VALETS[Math.floor(Math.random() * VALETS.length)],
            ticket: car.space.replace('Space ', '')
        };
        setLive(liveRequest);
        setNote(null);
    };

    const finish = message => {
        liveRequest = null;
        setLive(null);
        setNote(message ? { text: message, key: Date.now() } : null);
    };

    return (
        <div className="screen-content">
            <motion.div
                className="screen__head"
                initial={reduced ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
            >
                <div>
                    <span className="screen__eyebrow">Valet</span>
                    <h2 className="screen__title">Your vehicles</h2>
                </div>
            </motion.div>

            <div className="garage">
                {CARS.map((item, i) => {
                    const isOn = item.id === selected;
                    const coming = live && live.carId === item.id;

                    return (
                        <motion.button
                            type="button"
                            key={item.id}
                            className={`bay${isOn ? ' bay--on' : ''}`}
                            onClick={() => setSelected(item.id)}
                            aria-pressed={isOn}
                            initial={reduced ? false : { opacity: 0, y: 26 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.06 + i * 0.07, ease: EASE }}
                        >
                            {isOn && (
                                <motion.span
                                    className="bay__glow"
                                    layoutId="bay-glow"
                                    transition={{ type: 'spring', stiffness: 340, damping: 32 }}
                                />
                            )}

                            <span className="bay__photo">
                                <img src={item.image} alt={item.name} style={{ objectPosition: item.focus }} />
                            </span>

                            {coming && <span className={`bay__live${stage === STAGE[STAGE.length - 1] ? ' bay__live--ready' : ''}`}>{stage}</span>}

                            <span className="bay__body">
                                <span className="bay__space">{item.space}</span>
                                <span className="bay__name">{item.name}</span>
                                <span className="bay__colour">{item.colour}</span>
                            </span>
                        </motion.button>
                    );
                })}
            </div>

            {/* The request sits with the choice, and names the car, so the
                two are never read separately. Once made, the button gives
                way to the request itself, live until the car is out front. */}
            <AnimatePresence mode="wait" initial={false}>
                {live ? (
                    <LiveRequest key="live" request={live} onDone={finish} onStage={setStage} />
                ) : (
                    <motion.div
                        key="ask"
                        className="garage__action"
                        initial={reduced ? false : { opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.4, ease: EASE }}
                    >
                        <button type="button" className="btn btn-primary btn-large-request" onClick={request}>
                            Request the {car.name}
                        </button>
                        <p>Ready at the front entrance in about {ETA_MINUTES} minutes.</p>
                        {note && <div className="garage__note"><Note note={note} /></div>}
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="screen__head" style={{ marginTop: 'clamp(2rem, 4vw, 3rem)' }}>
                <h3>Valet services</h3>
                <span className="garage__for">for your {car.name}</span>
            </div>

            <div className="valet-grid">
                {SERVICES.map((service, i) => (
                    <ServiceCard key={service.key} service={service} car={car} index={i} />
                ))}
            </div>
        </div>
    );
}

/* A request on its way: the steps, a minutes-left figure and a bar that
   runs down to the car at the door, then a thank-you for the valet. */
function LiveRequest({ request, onDone, onStage }) {
    const reduced = useReducedMotion();
    const [now, setNow] = useState(() => Date.now());
    const [tip, setTip] = useState('');
    const car = CARS.find(c => c.id === request.carId) || CARS[0];

    const elapsed = Math.max(0, (now - request.startedAt) / 1000);
    const ready = elapsed >= RUN_SECONDS;
    const step = ready ? STEPS.length : STEPS.reduce((at, s, i) => (elapsed >= s.at ? i : at), 0);
    const minutesLeft = Math.max(1, Math.ceil(ETA_MINUTES * (1 - elapsed / RUN_SECONDS)));
    const arrival = new Date(request.startedAt + ETA_MINUTES * 60000)
        .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    useEffect(() => {
        if (ready) return undefined;
        const id = setInterval(() => setNow(Date.now()), 250);
        return () => clearInterval(id);
    }, [ready]);

    /* The bay above wears the same stage, so the car's own card agrees. */
    useEffect(() => { onStage(STAGE[Math.min(step, STAGE.length - 1)]); }, [step, onStage]);

    const title = ready
        ? `Your ${car.short} is at the front entrance`
        : step >= 2 ? `Your ${car.short} is on the way up` : `Getting your ${car.short}`;

    return (
        <motion.div
            className={`valet-live panel panel--accent${ready ? ' valet-live--ready' : ''}`}
            initial={reduced ? false : { opacity: 0, y: 16, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.55, ease: EASE }}
            role="status"
            aria-live="polite"
        >
            <div className="valet-live__head">
                <img className="valet-live__thumb" src={car.image} alt="" style={{ objectPosition: car.focus }} />
                <div className="valet-live__title">
                    <span className="screen__eyebrow">Ticket {request.ticket}</span>
                    <h4>{title}</h4>
                    <p className="row__meta">
                        {ready
                            ? `${request.valet} has it running with the climate on. Show ticket ${request.ticket} at the stand.`
                            : `${request.valet} is bringing it from ${car.space.toLowerCase()}. Expected out front at ${arrival}.`}
                    </p>
                </div>
                <div className="valet-live__eta" aria-label={ready ? 'Ready now' : `About ${minutesLeft} minutes left`}>
                    {ready ? (
                        <motion.span
                            className="valet-live__ready"
                            initial={reduced ? false : { scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5, ease: EASE }}
                        >
                            <Tick />
                        </motion.span>
                    ) : (
                        <>
                            <span className="valet-live__num">{minutesLeft}</span>
                            <span className="valet-live__unit">min</span>
                        </>
                    )}
                </div>
            </div>

            <div className="meter valet-live__meter">
                <span
                    className="meter__fill"
                    style={{ width: `${Math.min(100, (elapsed / RUN_SECONDS) * 100)}%`, transition: 'width 0.25s linear' }}
                />
            </div>

            <Steps steps={STEPS} current={step} label="Valet request progress" />

            {ready ? (
                <div className="valet-live__foot">
                    <div className="valet-live__tip">
                        <span className="form-label">Thank {request.valet}</span>
                        <Segments label="Tip" options={TIPS} value={tip} onChange={setTip} />
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => onDone(tip
                            ? `Enjoy the drive. Your ${tip} tip for ${request.valet} is on your unit account.`
                            : `Enjoy the drive. Your ${car.short} is checked out from ${car.space.toLowerCase()}.`)}
                    >
                        I have my car
                    </button>
                </div>
            ) : (
                <div className="valet-live__foot">
                    <span className="row__meta">You will get a text when it reaches the door.</span>
                    <button
                        type="button"
                        className="btn btn-outline-light btn-sm"
                        onClick={() => onDone(`Request cancelled. Your ${car.short} stays in ${car.space.toLowerCase()}.`)}
                    >
                        Cancel request
                    </button>
                </div>
            )}
        </motion.div>
    );
}

/* One valet service. Each checks its own fields and answers in the card,
   where the reader is looking, rather than at the top of a long screen. */
function ServiceCard({ service, car, index }) {
    const reduced = useReducedMotion();
    const { add } = useBuilding();
    const [values, setValues] = useState({});
    const [missing, setMissing] = useState([]);
    const [note, setNote] = useState(null);
    const timer = useRef(null);

    useEffect(() => () => clearTimeout(timer.current), []);

    const names = [
        ...(service.fields || []).map(f => f.name),
        ...(service.select ? [service.select.name] : []),
        ...(service.textarea ? [service.textarea.name] : [])
    ];

    const say = (text, tone = 'ok') => {
        clearTimeout(timer.current);
        setNote({ text, tone, key: Date.now() });
        if (tone === 'ok') timer.current = setTimeout(() => setNote(null), 7000);
    };

    const set = name => event => {
        const value = event.target.value;
        setValues(v => ({ ...v, [name]: value }));
        setMissing(m => m.filter(n => n !== name));
        if (note?.tone === 'warn') setNote(null);
    };

    const submit = () => {
        const empty = names.filter(n => !String(values[n] || '').trim());
        if (empty.length || (service.valid && !service.valid(values))) {
            setMissing(empty.length ? empty : names);
            return say(service.missing, 'warn');
        }

        /* A guest's valet spot is also a guest: the front desk sees them
           on its list, and so does the resident's own Guests screen. */
        if (service.key === 'guest') {
            add('visitors', {
                name: values.name,
                unit: RESIDENT.unit,
                kind: 'Guest',
                date: printDate(values.date),
                note: `Valet reserved, ${values.vehicle}`,
                status: 'Expected'
            });
        }

        say(service.confirm(values, car));
        setValues({});
        setMissing([]);
    };

    const invalid = name => (missing.includes(name) ? 'true' : undefined);

    return (
        <motion.div
            className="valet-card"
            initial={reduced ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.14 + index * 0.06, ease: EASE }}
        >
            <h4>{service.title}</h4>
            <p>{service.copy}</p>

            {service.fields?.map(field => (
                <input
                    key={field.name}
                    type={field.type}
                    className="form-control"
                    placeholder={field.placeholder}
                    aria-label={field.placeholder || field.label}
                    aria-invalid={invalid(field.name)}
                    value={values[field.name] || ''}
                    onChange={set(field.name)}
                    min={field.type === 'date' ? iso(0) : field.min}
                    max={field.max}
                    inputMode={field.type === 'number' ? 'numeric' : undefined}
                />
            ))}

            {service.select && (
                <select
                    className="form-control"
                    aria-label={service.title}
                    aria-invalid={invalid(service.select.name)}
                    value={values[service.select.name] || ''}
                    onChange={set(service.select.name)}
                >
                    <option value="" disabled>{service.select.placeholder}</option>
                    {service.select.options.map(option => <option key={option}>{option}</option>)}
                </select>
            )}

            {service.textarea && (
                <textarea
                    className="form-control"
                    rows="3"
                    placeholder={service.textarea.placeholder}
                    aria-label={service.title}
                    aria-invalid={invalid(service.textarea.name)}
                    value={values[service.textarea.name] || ''}
                    onChange={set(service.textarea.name)}
                />
            )}

            <Note note={note} />

            <button type="button" className="btn btn-primary" onClick={submit}>{service.action}</button>
        </motion.div>
    );
}

export default ValetScreen;
